// The company's legal and payout details (aldoo-na store/payouts/profile), in
// the parts the Company section's tabs show them — each saved as it is edited,
// like the rest of the section (the server keeps what is missing as missing):
//   CompanyIdentity   what kind of seller: a company (EIK, VAT number, МОЛ) or a person (EGN, date of birth)
//   CompanyAddress    the registered address and its country (the company's language)
//   PayoutEmailRole   where payout notices go: the company email, or another address
//   BankAccount       the IBAN, saved on purpose (it goes to the payout provider), never shown back but its last four;
//                     above it the company's own Stripe account (StripeAccount), when it is paid there instead
// A required field still missing is outlined in red until it is filled.
import { BuildingLibraryIcon, LinkIcon } from "@heroicons/react/24/outline"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Button from "aldoo-ra/Button"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import { showSuccessToast, showErrorToast } from "aldoo-ra/Toast"
import { showPromptDialogAsync } from "aldoo-ra/PromptDialog"
import { Field, TextInput, Select, DateInput, Toggle, Chip, Grid, MethodRow } from "../AdminShared/ui"
import { PayoutsOverview, PayoutsProfileSave, PayoutsStripeConnect } from "../AdminShared/api"
import usePortalTexts from "../Vendor/texts/usePortalTexts"
import useSiteLanguage from "../Storefront/hooks/useSiteLanguage"
import { CountryPicker } from "../AdminShared/CountriesSelect"
import { PAYOUTS } from "../Vendor/texts/bundles"

// the API's error codes, as a person reads them
const ERRORS = {
  required: "Required",
  invalid: "Not valid",
  email_invalid: "Not a valid email",
  company_id_invalid: "Not a valid EIK / BULSTAT",
  vat_invalid: "Not a valid VAT number",
  iban_invalid: "Not a valid IBAN",
  iban_required: "Required",
  complete_details_first: "Complete your company details and address first",
  payouts_secret_not_configured: "Payouts are not set up yet — please contact Aldoo",
  provider_error: "The bank account could not be added — check the IBAN",
}
const STATUS_CHIP = { active: "bg-green-100 text-green-800", pending_verification: "bg-blue-100 text-blue-800", failed: "bg-red-100 text-red-800", incomplete: "bg-amber-100 text-amber-800" }
const STATUS_LABEL = { active: "Ready for payouts", pending_verification: "Being verified", failed: "Rejected — check the details", incomplete: "Incomplete" }

/**
 * The payout profile of an organization: loaded once, saved in parts.
 * `save(fields)` → true, or the errors (also kept in `errors`, by field).
 */
// `onSaved`: after each save — the portal reloads what depends on it (the checklist, the guide)
export function usePayoutProfile(orgId, { enabled = true, onSaved } = {}) {
  const [profile, setProfile] = useState(undefined)
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const reload = useCallback(() => {
    if (!orgId || !enabled) return
    PayoutsOverview(orgId).then((res) => setProfile(res?.profile || {})).catch(() => setProfile({}))
  }, [orgId, enabled])
  useEffect(reload, [reload])
  const save = useCallback(
    async (fields) => {
      setSaving(true)
      const res = await PayoutsProfileSave({ orgId, ...fields }).catch((e) => ({ errors: { _: e?.message } }))
      setSaving(false)
      const keys = Object.keys(fields).flatMap((k) => (k === "address" ? Object.keys(fields.address || {}).map((a) => `address.${a}`) : [k]))
      if (res?.errors) {
        setErrors((e) => ({ ...Object.fromEntries(Object.entries(e).filter(([k]) => !keys.includes(k))), ...res.errors }))
        return res.errors
      }
      setErrors((e) => Object.fromEntries(Object.entries(e).filter(([k]) => !keys.includes(k))))
      setProfile(res)
      onSaved?.(res)
      return true
    },
    [orgId, onSaved]
  )
  // what the profile already holds that is not valid (an id that does not fit the country chosen after it)
  // is shown on its field too, until a save puts it right
  const shown = useMemo(() => ({ ...(profile?.invalid || {}), ...errors }), [profile, errors])
  // reload: the profile asked for again (the Stripe account disconnected), what depends on it told
  const reloadAll = useCallback(() => {
    reload()
    onSaved?.()
  }, [reload, onSaved])
  return { profile, errors: shown, saving, save, reload: reloadAll, loading: profile === undefined }
}

/** A text the part keeps while it is typed, saved when the field is left (and changed). */
function useDraft(value) {
  const [draft, setDraft] = useState(value ?? "")
  useEffect(() => setDraft(value ?? ""), [value])
  return [draft, setDraft]
}

function useErrorText() {
  const t = usePortalTexts(PAYOUTS)
  return (code) => (code ? t(ERRORS[code] || "Check this field") : null)
}

/** A text field of the profile: saved on leaving it; a required one missing is in red. */
function ProfileText({ label, value, onSave, error, required, canEdit, hint, testId, ...rest }) {
  const t = usePortalTexts(PAYOUTS)
  const [draft, setDraft] = useDraft(value)
  const missing = required && !String(draft || "").trim()
  // under an error the field is checked again as it changes (a value pasted in, a digit put right): a moment
  // after the last change, not only on leaving it
  const recheck = useRef(null)
  useEffect(() => () => clearTimeout(recheck.current), [])
  const change = (next) => {
    setDraft(next)
    clearTimeout(recheck.current)
    if (!error || !String(next).trim()) return
    recheck.current = setTimeout(() => onSave(String(next).trim()), 500)
  }
  const leave = () => {
    clearTimeout(recheck.current)
    if (String(draft) !== String(value ?? "") || error) onSave(String(draft).trim())
  }
  return (
    <Field label={label} error={error || (missing ? t`Required` : null)} hint={hint}>
      {/* saved when changed — or, under an error, when put back to the saved value: the save clears the error */}
      <TextInput value={draft} onChange={change} onBlur={leave} disabled={!canEdit} data-testid={testId} {...rest} />
    </Field>
  )
}

export function CompanyIdentity({ payout, canEdit }) {
  const t = usePortalTexts(PAYOUTS)
  const errorText = useErrorText()
  const p = payout.profile || {}
  const company = p.entityType !== "individual"
  const thisYear = new Date().getFullYear()
  return (
    <Grid cols={2}>
      <Field label={t`Paid to`}>
        <Select
          value={p.entityType || "company"}
          onChange={(v) => payout.save({ entityType: v || "company" })}
          options={[
            { value: "company", label: t`A company` },
            { value: "individual", label: t`A person (sole trader)` },
          ]}
          disabled={!canEdit}
          data-testid="payout-entity-type"
        />
      </Field>
      {company ? (
        <>
          <ProfileText label={t`EIK / BULSTAT`} value={p.companyId} onSave={(v) => payout.save({ companyId: v })} error={errorText(payout.errors.companyId)} required canEdit={canEdit} testId="payout-company-id" />
          <ProfileText label={t`VAT number (if registered)`} value={p.vatNumber} onSave={(v) => payout.save({ vatNumber: v })} error={errorText(payout.errors.vatNumber)} canEdit={canEdit} placeholder="BG…" testId="payout-vat" />
          <ProfileText label={t`Represented by`} value={p.representative} onSave={(v) => payout.save({ representative: v })} error={errorText(payout.errors.representative)} required canEdit={canEdit} testId="payout-representative" />
        </>
      ) : (
        <>
          <ProfileText label={t`Personal ID (EGN)`} value={p.personalId} onSave={(v) => payout.save({ personalId: v })} error={errorText(payout.errors.personalId)} required canEdit={canEdit} testId="payout-personal-id" />
          <Field label={t`Date of birth`} error={errorText(payout.errors.dateOfBirth) || (!p.dateOfBirth ? t`Required` : null)}>
            <DateInput
              value={p.dateOfBirth ? String(p.dateOfBirth).slice(0, 10) : null}
              onChange={(v) => payout.save({ dateOfBirth: v })}
              disabled={!canEdit}
              clearable={false}
              placeholder={t`Select date`}
              minYear={thisYear - 110}
              maxYear={thisYear}
              defaultViewDate={`${thisYear - 30}-01-01`}
              data-testid="payout-dob"
            />
          </Field>
        </>
      )}
    </Grid>
  )
}

export function CompanyAddress({ payout, canEdit }) {
  const t = usePortalTexts(PAYOUTS)
  const errorText = useErrorText()
  const a = payout.profile?.address || {}
  const saveAddress = (key) => (v) => payout.save({ address: { [key]: v } })
  const language = useSiteLanguage()
  const country = payout.profile?.country || ""
  return (
    <Grid cols={2}>
      {/* where the company is registered: required, and the language Aldoo writes to it in (emails, payout reports) */}
      <Field label={t`Country`} hint={t`The language of Aldoo's emails and payout reports to you follows it`} error={errorText(payout.errors.country) || (!country ? t`Required` : null)}>
        <CountryPicker value={country} onChange={(v) => payout.save({ country: v })} language={language} invalid={!country} disabled={!canEdit} placeholder={t`Pick a country…`} data-testid="payout-country" />
      </Field>
      <span className="hidden sm:block" />
      <ProfileText label={t`Address`} value={a.line1} onSave={saveAddress("line1")} error={errorText(payout.errors["address.line1"])} required canEdit={canEdit} testId="payout-address" />
      <ProfileText label={t`Address, line 2 (optional)`} value={a.line2} onSave={saveAddress("line2")} canEdit={canEdit} testId="payout-address-2" />
      <ProfileText label={t`City`} value={a.city} onSave={saveAddress("city")} error={errorText(payout.errors["address.city"])} required canEdit={canEdit} testId="payout-city" />
      <ProfileText label={t`Postal code`} value={a.postalCode} onSave={saveAddress("postalCode")} error={errorText(payout.errors["address.postalCode"])} required canEdit={canEdit} testId="payout-postal" />
    </Grid>
  )
}

/**
 * One of the company's emails by its role: the company email unless "use
 * another email" is on, then its own address. `own` / `value` say how it is
 * now; `onChange({ own, email })` saves it.
 */
export function EmailRole({ title, hint, primary, own, value, onChange, error, canEdit, testId, active, onActiveChange }) {
  const t = usePortalTexts(PAYOUTS)
  const [draft, setDraft] = useDraft(own ? value : "")
  // notices that can be turned off: a switch by the title, the state beside it
  const switchable = typeof onActiveChange === "function"
  return (
    <div className="flex flex-col gap-2 rounded-md border border-admin_bordered p-3" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-3">
            <div className="text-sm font-medium">{title}</div>
            {switchable && (
              <div className="flex items-center gap-2 text-xs" data-testid={`${testId}-active`} data-active={active ? "on" : "off"}>
                <ToggleSwitch variant="admin" isOn={!!active} onChange={(on) => canEdit && onActiveChange(!!on)} />
                <span className={active ? "text-admin_success font-semibold" : "text-admin_text_secondary"}>{active ? t`Notices on` : t`Notices off`}</span>
              </div>
            )}
          </div>
          {hint && <div className="text-xs text-admin_text_secondary">{hint}</div>}
        </div>
        {/* a div, not a label: a label would hand the click on to the switch a second time */}
        <div className="flex items-center gap-2 text-sm">
          <ToggleSwitch variant="admin" isOn={own} onChange={(on) => canEdit && onChange({ own: !!on, email: on ? draft : "" })} />
          {t`Use another email`}
        </div>
      </div>
      {own ? (
        <Field error={error || (!String(draft || "").trim() ? t`Enter the address, or turn this off to use the company email` : null)}>
          <TextInput value={draft} onChange={setDraft} onBlur={() => onChange({ own: true, email: String(draft).trim() })} type="email" placeholder={primary || "name@example.com"} disabled={!canEdit} data-testid={`${testId}-input`} />
        </Field>
      ) : (
        <div className="text-sm text-admin_text_secondary" data-testid={`${testId}-primary`}>
          {primary ? t("Goes to the company email: %s", primary) : t`Set the company email above`}
        </div>
      )}
    </div>
  )
}

/** Where payout notices go — a role of the Contacts tab. */
export function PayoutEmailRole({ payout, primary, canEdit }) {
  const t = usePortalTexts(PAYOUTS)
  const errorText = useErrorText()
  const p = payout.profile || {}
  return (
    <EmailRole
      title={t`Payout notices`}
      hint={t`Payouts made, and anything about your bank account`}
      primary={primary}
      own={p.emailMode === "own"}
      value={p.email}
      onChange={({ own, email }) => payout.save(own ? { emailMode: "own", ...(email ? { email } : {}) } : { emailMode: "contact" })}
      error={errorText(payout.errors.email)}
      canEdit={canEdit}
      testId="payout-email"
      // the payout notices mailed or not (the contract's always are)
      active={p.notify !== false}
      onActiveChange={(on) => payout.save({ notify: on })}
    />
  )
}

// what Stripe's page sent the person back with (?stripe_connect=…), as they read it
const CONNECT_RESULTS = {
  connected: ["ok", "Your Stripe account is now connected"],
  denied: ["error", "The Stripe account was not connected: the connection was not allowed"],
  expired: ["error", "The connection took too long: please try again"],
  failed: ["error", "The Stripe account could not be connected: please try again"],
}

/**
 * The company's own Stripe account (store/payouts/stripe-connect): "Connect Stripe" opens Stripe's page,
 * where the company signs in and allows Aldoo to send to it; Stripe sends them back here. Connected, its
 * payouts are transfers to that account in the monthly payouts Aldoo approves, and no bank account is
 * needed for them. Shown only where the platform set Stripe Connect up.
 */
export function StripeAccount({ payout, orgId, canEdit }) {
  const t = usePortalTexts(PAYOUTS)
  const p = payout.profile || {}
  const [busy, setBusy] = useState(false)
  // back from Stripe: what happened, said once, and the mark taken off the address
  useEffect(() => {
    if (typeof window === "undefined") return
    const url = new URL(window.location.href)
    const result = CONNECT_RESULTS[url.searchParams.get("stripe_connect")]
    if (!result) return
    ;(result[0] === "ok" ? showSuccessToast : showErrorToast)(t(result[1]))
    url.searchParams.delete("stripe_connect")
    window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash)
  }, [])
  if (!p.stripeConnectAvailable && !p.stripeConnect?.accountId) return null
  const connected = p.stripeConnect?.accountId
  const connect = async () => {
    setBusy(true)
    const returnTo = window.location.pathname + window.location.search
    const res = await PayoutsStripeConnect(orgId, "start", { returnTo }).catch((e) => ({ error: e?.message }))
    if (!res?.url) {
      setBusy(false)
      return showErrorToast(t`The Stripe account could not be connected: please try again`, res?.message || res?.error)
    }
    window.location.assign(res.url)
  }
  const disconnect = async () => {
    const ok = await showPromptDialogAsync({
      variant: "admin",
      title: t`Disconnect the Stripe account?`,
      message: p.ibanLast4 ? t`Your payouts go to your bank account again.` : t`Your payouts wait until you connect a Stripe account again or add a bank account.`,
      confirmText: t`Disconnect`,
      cancelText: t`Cancel`,
    })
    if (!ok) return
    setBusy(true)
    const res = await PayoutsStripeConnect(orgId, "disconnect").catch((e) => ({ error: e?.message }))
    setBusy(false)
    if (res?.error || res?.errors) return showErrorToast(t`The Stripe account could not be disconnected`, res?.message || res?.error)
    payout.reload?.()
    showSuccessToast(t`The Stripe account is disconnected`)
  }
  return (
    <MethodRow
      icon={LinkIcon}
      title={t`Stripe account`}
      help={t`Already selling with Stripe? Connect your Stripe account and Aldoo pays your sales into it instead of a bank account.`}
      testId="payout-stripe-account"
      value={connected ? <span className="flex items-center gap-2" data-testid="payout-stripe-account-current"><span className="font-semibold">{p.stripeConnect.name || "Stripe"}</span><span className="font-mono text-xs text-admin_text_secondary">{connected}</span></span> : null}
      empty={t`Not connected`}
      chip={connected ? <Chip className={STATUS_CHIP.active}>{t`Connected`}</Chip> : null}
      actions={
        canEdit ? (
          connected ? (
            <Button variant="admin_text" className="text-admin_error" disabled={busy} onClick={disconnect} data-testid="payout-stripe-disconnect">
              {t`Disconnect`}
            </Button>
          ) : (
            <Button variant="admin_secondary" disabled={busy} onClick={connect} data-testid="payout-stripe-connect">
              {busy ? t`Opening Stripe…` : t`Connect Stripe`}
            </Button>
          )
        ) : null
      }
    >
      {connected && (
        <p className="m-0 text-sm text-admin_text_secondary" data-testid="payout-stripe-connected-note">
          {t`Your Stripe account is now connected. Your payouts are sent to it as part of the monthly payouts, once Aldoo approves them.`}
        </p>
      )}
    </MethodRow>
  )
}

/**
 * The IBAN: shown as its last four; none yet, "Add bank account" opens the form (as Connect Stripe beside it);
 * a new one typed after "Change", saved with a confirmation.
 */
export function BankAccount({ payout, canEdit, orgId }) {
  const t = usePortalTexts(PAYOUTS)
  const errorText = useErrorText()
  const p = payout.profile || {}
  const [changing, setChanging] = useState(false)
  const [iban, setIban] = useState("")
  const [confirm, setConfirm] = useState(false)
  useEffect(() => setChanging(false), [p.ibanLast4])
  const save = async () => {
    const res = await payout.save({ iban, acceptTerms: true })
    if (res !== true) return showErrorToast(t`The bank account could not be saved`, errorText(res.iban) || res._)
    setIban("")
    setConfirm(false)
    setChanging(false)
    showSuccessToast(t`Payout details saved`)
  }
  // incomplete says nothing more than the tabs' red marks and the fields in red already do: the
  // state shows once the details are in (under review, active, refused and why)
  const incomplete = !p.status || p.status === "incomplete"
  return (
    <div className="flex flex-col gap-4" data-testid="bank-account">
      {orgId && <StripeAccount payout={payout} orgId={orgId} canEdit={canEdit} />}
      {/* as every payment method is shown (the commission's card and bank account too): Change opens the form in place */}
      <MethodRow
        icon={BuildingLibraryIcon}
        title={t`Bank account`}
        help={t`Your bank account in EUR. We keep only its last four characters.`}
        testId="payout-iban-row"
        value={p.ibanLast4 && !changing ? <span className="flex items-center gap-2" data-testid="payout-iban-current"><span className="font-semibold">IBAN</span><span className="font-mono tracking-wider">{p.ibanCountry ? `${p.ibanCountry} ` : ""}•••• {p.ibanLast4}</span></span> : null}
        empty={changing ? "" : t`No bank account yet`}
        chip={
          !incomplete && !changing ? (
            <span className="flex items-center gap-2 text-sm">
              <Chip className={STATUS_CHIP[p.status] || STATUS_CHIP.incomplete}>{t(STATUS_LABEL[p.status] || STATUS_LABEL.incomplete)}</Chip>
              {p.statusReason && p.status !== "active" && <span className="text-admin_text_secondary">({p.statusReason})</span>}
            </span>
          ) : null
        }
        actions={
          canEdit && !changing ? (
            <Button variant="admin_secondary" onClick={() => setChanging(true)} data-testid={p.ibanLast4 ? "payout-iban-change" : "payout-iban-add"}>
              {p.ibanLast4 ? t`Change` : t`Add bank account`}
            </Button>
          ) : null
        }
      >
        {changing && (
          // the IBAN, its confirmation under it, then Save and Cancel side by side
          <div className="flex flex-col items-start gap-3 w-full">
            <div className="flex flex-col gap-1 sm:max-w-[calc(50%-6px)] w-full">
              <TextInput value={iban} onChange={setIban} disabled={!canEdit} autoComplete="off" placeholder="BG00 XXXX 0000 0000 0000 00" className="w-full" data-testid="payout-iban" />
              {/* "required" is said for the Payouts step as a whole (PaymentsSteps payoutMissing); here only a refused IBAN */}
              {errorText(payout.errors.iban) && <span className="text-[11px] text-admin_error">{errorText(payout.errors.iban)}</span>}
            </div>
            {canEdit && (
              <>
                {/* the account is only paid into: the commission is paid by card or by hand, no debit mandate asked for */}
                <Toggle checked={confirm} onChange={setConfirm} label={t`I confirm these details are correct and that the account is in the name of the company / person above`} data-testid="payout-confirm" />
                <div className="flex flex-col items-stretch sm:flex-row sm:items-center gap-2">
                  <Button variant="admin_primary" onClick={save} disabled={payout.saving || !confirm || !iban.trim()} data-testid="payout-details-save">
                    {payout.saving ? t`Saving…` : t`Save the bank account`}
                  </Button>
                  {canEdit && (
                    <Button variant="admin_secondary" onClick={() => (setIban(""), setConfirm(false), setChanging(false))} disabled={payout.saving} data-testid="payout-iban-cancel">
                      {t`Cancel`}
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </MethodRow>
    </div>
  )
}
